import { ArchitectureTemplate, ComponentType, ConnectionType } from '../types';
import { MarkerType } from 'reactflow';

const idGen = (base: string, index: number) => `${base}-${index}`;

export const ARCHITECTURE_TEMPLATES: ArchitectureTemplate[] = [
  // 1. 3-Tier Web Application
  {
    id: 'three-tier-web',
    name: '3-Tier Web Application',
    description: 'Classic 3-tier architecture with load balancer, web servers, and database with caching layer',
    category: 'web',
    difficulty: 'beginner',
    thumbnail: '',
    nodes: [
      {
        id: idGen('lb', 1),
        type: 'custom',
        position: { x: 400, y: 50 },
        data: { label: 'LoadBalancer', type: ComponentType.LOAD_BALANCER }
      },
      {
        id: idGen('web', 1),
        type: 'custom',
        position: { x: 400, y: 180 },
        data: { label: 'WebServer Cluster', type: ComponentType.WEB_SERVER, isCluster: true }
      },
      {
        id: idGen('db', 1),
        type: 'custom',
        position: { x: 300, y: 340 },
        data: { label: 'Primary DB', type: ComponentType.DATABASE }
      },
      {
        id: idGen('cache', 1),
        type: 'custom',
        position: { x: 500, y: 340 },
        data: { label: 'Cache', type: ComponentType.CACHE }
      }
    ],
    edges: [
      {
        id: idGen('edge', 1),
        source: idGen('lb', 1),
        target: idGen('web', 1),
        animated: true,
        type: 'straight',
        label: ConnectionType.HTTPS,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.HTTPS }
      },
      {
        id: idGen('edge', 2),
        source: idGen('web', 1),
        target: idGen('db', 1),
        animated: true,
        type: 'straight',
        label: ConnectionType.JDBC,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.JDBC }
      },
      {
        id: idGen('edge', 3),
        source: idGen('web', 1),
        target: idGen('cache', 1),
        animated: true,
        type: 'straight',
        label: ConnectionType.TCP,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.TCP }
      }
    ]
  },

  // 2. Microservices Architecture
  {
    id: 'microservices',
    name: 'Microservices Architecture',
    description: 'API Gateway routing to multiple microservices with message broker and databases',
    category: 'microservices',
    difficulty: 'intermediate',
    thumbnail: '',
    nodes: [
      {
        id: idGen('gw', 2),
        type: 'custom',
        position: { x: 400, y: 30 },
        data: { label: 'API Gateway', type: ComponentType.API_GATEWAY }
      },
      {
        id: idGen('ms', 1),
        type: 'custom',
        position: { x: 150, y: 170 },
        data: { label: 'Auth Service', type: ComponentType.MICROSERVICE, isCluster: true }
      },
      {
        id: idGen('ms', 2),
        type: 'custom',
        position: { x: 400, y: 170 },
        data: { label: 'User Service', type: ComponentType.MICROSERVICE, isCluster: true }
      },
      {
        id: idGen('ms', 3),
        type: 'custom',
        position: { x: 650, y: 170 },
        data: { label: 'Order Service', type: ComponentType.MICROSERVICE, isCluster: true }
      },
      {
        id: idGen('broker', 1),
        type: 'custom',
        position: { x: 400, y: 320 },
        data: { label: 'Message Broker', type: ComponentType.MESSAGE_BROKER }
      },
      {
        id: idGen('db', 2),
        type: 'custom',
        position: { x: 150, y: 450 },
        data: { label: 'Auth DB', type: ComponentType.DATABASE }
      },
      {
        id: idGen('db', 3),
        type: 'custom',
        position: { x: 650, y: 450 },
        data: { label: 'Order DB', type: ComponentType.DATABASE }
      }
    ],
    edges: [
      {
        id: idGen('edge', 10),
        source: idGen('gw', 2),
        target: idGen('ms', 1),
        animated: true,
        type: 'straight',
        label: ConnectionType.HTTP,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.HTTP }
      },
      {
        id: idGen('edge', 11),
        source: idGen('gw', 2),
        target: idGen('ms', 2),
        animated: true,
        type: 'straight',
        label: ConnectionType.HTTP,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.HTTP }
      },
      {
        id: idGen('edge', 12),
        source: idGen('gw', 2),
        target: idGen('ms', 3),
        animated: true,
        type: 'straight',
        label: ConnectionType.HTTP,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.HTTP }
      },
      {
        id: idGen('edge', 13),
        source: idGen('ms', 1),
        target: idGen('broker', 1),
        animated: true,
        type: 'straight',
        label: ConnectionType.AMQP,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.AMQP }
      },
      {
        id: idGen('edge', 14),
        source: idGen('ms', 3),
        target: idGen('broker', 1),
        animated: true,
        type: 'straight',
        label: ConnectionType.AMQP,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.AMQP }
      },
      {
        id: idGen('edge', 15),
        source: idGen('ms', 1),
        target: idGen('db', 2),
        animated: true,
        type: 'straight',
        label: ConnectionType.JDBC,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.JDBC }
      },
      {
        id: idGen('edge', 16),
        source: idGen('ms', 3),
        target: idGen('db', 3),
        animated: true,
        type: 'straight',
        label: ConnectionType.JDBC,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.JDBC }
      }
    ]
  },

  // 3. Event-Driven System
  {
    id: 'event-driven',
    name: 'Event-Driven System',
    description: 'Asynchronous event processing with message broker, workers, and persistent storage',
    category: 'microservices',
    difficulty: 'intermediate',
    thumbnail: '',
    nodes: [
      {
        id: idGen('gw', 3),
        type: 'custom',
        position: { x: 400, y: 50 },
        data: { label: 'API Gateway', type: ComponentType.API_GATEWAY }
      },
      {
        id: idGen('broker', 2),
        type: 'custom',
        position: { x: 400, y: 180 },
        data: { label: 'Event Bus', type: ComponentType.MESSAGE_BROKER }
      },
      {
        id: idGen('worker', 1),
        type: 'custom',
        position: { x: 200, y: 310 },
        data: { label: 'Worker Pool', type: ComponentType.WORKER }
      },
      {
        id: idGen('worker', 2),
        type: 'custom',
        position: { x: 600, y: 310 },
        data: { label: 'Notification Worker', type: ComponentType.WORKER }
      },
      {
        id: idGen('db', 4),
        type: 'custom',
        position: { x: 200, y: 450 },
        data: { label: 'Event Store', type: ComponentType.DATABASE }
      },
      {
        id: idGen('storage', 1),
        type: 'custom',
        position: { x: 600, y: 450 },
        data: { label: 'Object Storage', type: ComponentType.STORAGE }
      }
    ],
    edges: [
      {
        id: idGen('edge', 20),
        source: idGen('gw', 3),
        target: idGen('broker', 2),
        animated: true,
        type: 'straight',
        label: ConnectionType.HTTP,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.HTTP }
      },
      {
        id: idGen('edge', 21),
        source: idGen('broker', 2),
        target: idGen('worker', 1),
        animated: true,
        type: 'straight',
        label: ConnectionType.AMQP,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.AMQP }
      },
      {
        id: idGen('edge', 22),
        source: idGen('broker', 2),
        target: idGen('worker', 2),
        animated: true,
        type: 'straight',
        label: ConnectionType.AMQP,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.AMQP }
      },
      {
        id: idGen('edge', 23),
        source: idGen('worker', 1),
        target: idGen('db', 4),
        animated: true,
        type: 'straight',
        label: ConnectionType.JDBC,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.JDBC }
      },
      {
        id: idGen('edge', 24),
        source: idGen('worker', 2),
        target: idGen('storage', 1),
        animated: true,
        type: 'straight',
        label: ConnectionType.HTTP,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.HTTP }
      }
    ]
  },

  // 4. Serverless Architecture
  {
    id: 'serverless',
    name: 'Serverless Architecture',
    description: 'Cloud-native serverless design with API Gateway, functions, and managed services',
    category: 'serverless',
    difficulty: 'beginner',
    thumbnail: '',
    nodes: [
      {
        id: idGen('gw', 4),
        type: 'custom',
        position: { x: 400, y: 50 },
        data: { label: 'API Gateway', type: ComponentType.API_GATEWAY }
      },
      {
        id: idGen('fn', 1),
        type: 'custom',
        position: { x: 200, y: 180 },
        data: { label: 'Auth Function', type: ComponentType.FUNCTION }
      },
      {
        id: idGen('fn', 2),
        type: 'custom',
        position: { x: 400, y: 180 },
        data: { label: 'API Function', type: ComponentType.FUNCTION }
      },
      {
        id: idGen('fn', 3),
        type: 'custom',
        position: { x: 600, y: 180 },
        data: { label: 'Process Function', type: ComponentType.FUNCTION }
      },
      {
        id: idGen('db', 5),
        type: 'custom',
        position: { x: 400, y: 310 },
        data: { label: 'NoSQL DB', type: ComponentType.DATABASE }
      },
      {
        id: idGen('storage', 2),
        type: 'custom',
        position: { x: 600, y: 310 },
        data: { label: 'S3 Storage', type: ComponentType.STORAGE }
      }
    ],
    edges: [
      {
        id: idGen('edge', 30),
        source: idGen('gw', 4),
        target: idGen('fn', 1),
        animated: true,
        type: 'straight',
        label: ConnectionType.HTTPS,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.HTTPS }
      },
      {
        id: idGen('edge', 31),
        source: idGen('gw', 4),
        target: idGen('fn', 2),
        animated: true,
        type: 'straight',
        label: ConnectionType.HTTPS,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.HTTPS }
      },
      {
        id: idGen('edge', 32),
        source: idGen('gw', 4),
        target: idGen('fn', 3),
        animated: true,
        type: 'straight',
        label: ConnectionType.HTTPS,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.HTTPS }
      },
      {
        id: idGen('edge', 33),
        source: idGen('fn', 2),
        target: idGen('db', 5),
        animated: true,
        type: 'straight',
        label: ConnectionType.HTTPS,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.HTTPS }
      },
      {
        id: idGen('edge', 34),
        source: idGen('fn', 3),
        target: idGen('storage', 2),
        animated: true,
        type: 'straight',
        label: ConnectionType.HTTPS,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.HTTPS }
      }
    ]
  },

  // 5. CQRS Pattern
  {
    id: 'cqrs-pattern',
    name: 'CQRS Pattern',
    description: 'Command Query Responsibility Segregation with separate read/write databases',
    category: 'data',
    difficulty: 'advanced',
    thumbnail: '',
    nodes: [
      {
        id: idGen('gw', 5),
        type: 'custom',
        position: { x: 400, y: 30 },
        data: { label: 'API Gateway', type: ComponentType.API_GATEWAY }
      },
      {
        id: idGen('write', 1),
        type: 'custom',
        position: { x: 200, y: 160 },
        data: { label: 'Command API', type: ComponentType.MICROSERVICE }
      },
      {
        id: idGen('read', 1),
        type: 'custom',
        position: { x: 600, y: 160 },
        data: { label: 'Query API', type: ComponentType.MICROSERVICE }
      },
      {
        id: idGen('writedb', 1),
        type: 'custom',
        position: { x: 200, y: 310 },
        data: { label: 'Write DB', type: ComponentType.DATABASE }
      },
      {
        id: idGen('readdb', 1),
        type: 'custom',
        position: { x: 600, y: 310 },
        data: { label: 'Read DB', type: ComponentType.DATABASE }
      },
      {
        id: idGen('sync', 1),
        type: 'custom',
        position: { x: 400, y: 360 },
        data: { label: 'Event Sync', type: ComponentType.WORKER }
      }
    ],
    edges: [
      {
        id: idGen('edge', 40),
        source: idGen('gw', 5),
        target: idGen('write', 1),
        animated: true,
        type: 'straight',
        label: 'POST/PUT',
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.HTTP }
      },
      {
        id: idGen('edge', 41),
        source: idGen('gw', 5),
        target: idGen('read', 1),
        animated: true,
        type: 'straight',
        label: 'GET',
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.HTTP }
      },
      {
        id: idGen('edge', 42),
        source: idGen('write', 1),
        target: idGen('writedb', 1),
        animated: true,
        type: 'straight',
        label: ConnectionType.JDBC,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.JDBC }
      },
      {
        id: idGen('edge', 43),
        source: idGen('read', 1),
        target: idGen('readdb', 1),
        animated: true,
        type: 'straight',
        label: ConnectionType.JDBC,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.JDBC }
      },
      {
        id: idGen('edge', 44),
        source: idGen('writedb', 1),
        target: idGen('sync', 1),
        animated: true,
        type: 'straight',
        label: 'CDC',
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.TCP }
      },
      {
        id: idGen('edge', 45),
        source: idGen('sync', 1),
        target: idGen('readdb', 1),
        animated: true,
        type: 'straight',
        label: 'Sync',
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.JDBC }
      }
    ]
  },

  // 6. Kubernetes Cluster
  {
    id: 'kubernetes',
    name: 'Kubernetes Cluster',
    description: 'Container orchestration with ingress, services, and persistent volumes',
    category: 'kubernetes',
    difficulty: 'advanced',
    thumbnail: '',
    nodes: [
      {
        id: idGen('ingress', 1),
        type: 'custom',
        position: { x: 400, y: 30 },
        data: { label: 'Ingress', type: ComponentType.LOAD_BALANCER }
      },
      {
        id: idGen('k8s', 1),
        type: 'custom',
        position: { x: 400, y: 160 },
        data: { label: 'K8s Cluster', type: ComponentType.KUBERNETES }
      },
      {
        id: idGen('svc', 1),
        type: 'custom',
        position: { x: 200, y: 290 },
        data: { label: 'Frontend Service', type: ComponentType.MICROSERVICE }
      },
      {
        id: idGen('svc', 2),
        type: 'custom',
        position: { x: 400, y: 290 },
        data: { label: 'Backend Service', type: ComponentType.MICROSERVICE }
      },
      {
        id: idGen('svc', 3),
        type: 'custom',
        position: { x: 600, y: 290 },
        data: { label: 'Worker Service', type: ComponentType.WORKER }
      },
      {
        id: idGen('pv', 1),
        type: 'custom',
        position: { x: 400, y: 420 },
        data: { label: 'Persistent Volume', type: ComponentType.STORAGE }
      }
    ],
    edges: [
      {
        id: idGen('edge', 50),
        source: idGen('ingress', 1),
        target: idGen('k8s', 1),
        animated: true,
        type: 'straight',
        label: ConnectionType.HTTPS,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.HTTPS }
      },
      {
        id: idGen('edge', 51),
        source: idGen('k8s', 1),
        target: idGen('svc', 1),
        animated: true,
        type: 'straight',
        label: ConnectionType.INTERNAL,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.INTERNAL }
      },
      {
        id: idGen('edge', 52),
        source: idGen('k8s', 1),
        target: idGen('svc', 2),
        animated: true,
        type: 'straight',
        label: ConnectionType.INTERNAL,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.INTERNAL }
      },
      {
        id: idGen('edge', 53),
        source: idGen('k8s', 1),
        target: idGen('svc', 3),
        animated: true,
        type: 'straight',
        label: ConnectionType.INTERNAL,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.INTERNAL }
      },
      {
        id: idGen('edge', 54),
        source: idGen('svc', 2),
        target: idGen('pv', 1),
        animated: true,
        type: 'straight',
        label: 'Mount',
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.INTERNAL }
      }
    ]
  },

  // 7. CDN + Web Architecture
  {
    id: 'cdn-web',
    name: 'CDN + Web Architecture',
    description: 'Global content delivery with CDN, web servers, and backend services',
    category: 'web',
    difficulty: 'intermediate',
    thumbnail: '',
    nodes: [
      {
        id: idGen('cdn', 1),
        type: 'custom',
        position: { x: 400, y: 30 },
        data: { label: 'CDN', type: ComponentType.CDN }
      },
      {
        id: idGen('lb', 2),
        type: 'custom',
        position: { x: 400, y: 150 },
        data: { label: 'Load Balancer', type: ComponentType.LOAD_BALANCER }
      },
      {
        id: idGen('web', 2),
        type: 'custom',
        position: { x: 400, y: 270 },
        data: { label: 'Web Servers', type: ComponentType.WEB_SERVER, isCluster: true }
      },
      {
        id: idGen('cache', 2),
        type: 'custom',
        position: { x: 250, y: 400 },
        data: { label: 'Redis Cache', type: ComponentType.CACHE }
      },
      {
        id: idGen('db', 6),
        type: 'custom',
        position: { x: 550, y: 400 },
        data: { label: 'Database', type: ComponentType.DATABASE }
      },
      {
        id: idGen('storage', 3),
        type: 'custom',
        position: { x: 100, y: 150 },
        data: { label: 'Static Assets', type: ComponentType.STORAGE }
      }
    ],
    edges: [
      {
        id: idGen('edge', 60),
        source: idGen('cdn', 1),
        target: idGen('storage', 3),
        animated: true,
        type: 'straight',
        label: ConnectionType.HTTPS,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.HTTPS }
      },
      {
        id: idGen('edge', 61),
        source: idGen('cdn', 1),
        target: idGen('lb', 2),
        animated: true,
        type: 'straight',
        label: ConnectionType.HTTPS,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.HTTPS }
      },
      {
        id: idGen('edge', 62),
        source: idGen('lb', 2),
        target: idGen('web', 2),
        animated: true,
        type: 'straight',
        label: ConnectionType.HTTP,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.HTTP }
      },
      {
        id: idGen('edge', 63),
        source: idGen('web', 2),
        target: idGen('cache', 2),
        animated: true,
        type: 'straight',
        label: ConnectionType.TCP,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.TCP }
      },
      {
        id: idGen('edge', 64),
        source: idGen('web', 2),
        target: idGen('db', 6),
        animated: true,
        type: 'straight',
        label: ConnectionType.JDBC,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.JDBC }
      }
    ]
  },

  // 8. Secure Gateway Architecture
  {
    id: 'secure-gateway',
    name: 'Secure Gateway Architecture',
    description: 'Security-focused design with firewall, authentication, and encrypted connections',
    category: 'web',
    difficulty: 'intermediate',
    thumbnail: '',
    nodes: [
      {
        id: idGen('fw', 1),
        type: 'custom',
        position: { x: 400, y: 30 },
        data: { label: 'Firewall', type: ComponentType.FIREWALL }
      },
      {
        id: idGen('gw', 6),
        type: 'custom',
        position: { x: 400, y: 150 },
        data: { label: 'API Gateway', type: ComponentType.API_GATEWAY }
      },
      {
        id: idGen('auth', 1),
        type: 'custom',
        position: { x: 200, y: 270 },
        data: { label: 'Auth Service', type: ComponentType.AUTH_SERVICE }
      },
      {
        id: idGen('app', 1),
        type: 'custom',
        position: { x: 600, y: 270 },
        data: { label: 'App Services', type: ComponentType.MICROSERVICE, isCluster: true }
      },
      {
        id: idGen('db', 7),
        type: 'custom',
        position: { x: 400, y: 400 },
        data: { label: 'Database', type: ComponentType.DATABASE }
      }
    ],
    edges: [
      {
        id: idGen('edge', 70),
        source: idGen('fw', 1),
        target: idGen('gw', 6),
        animated: true,
        type: 'straight',
        label: ConnectionType.HTTPS,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.HTTPS }
      },
      {
        id: idGen('edge', 71),
        source: idGen('gw', 6),
        target: idGen('auth', 1),
        animated: true,
        type: 'straight',
        label: ConnectionType.HTTPS,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.HTTPS }
      },
      {
        id: idGen('edge', 72),
        source: idGen('gw', 6),
        target: idGen('app', 1),
        animated: true,
        type: 'straight',
        label: ConnectionType.HTTPS,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.HTTPS }
      },
      {
        id: idGen('edge', 73),
        source: idGen('auth', 1),
        target: idGen('db', 7),
        animated: true,
        type: 'straight',
        label: ConnectionType.JDBC,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.JDBC }
      },
      {
        id: idGen('edge', 74),
        source: idGen('app', 1),
        target: idGen('db', 7),
        animated: true,
        type: 'straight',
        label: ConnectionType.JDBC,
        style: { stroke: '#000000', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#000000' },
        data: { type: ConnectionType.JDBC }
      }
    ]
  }
];

// Helper to get templates by category
export const getTemplatesByCategory = (category: string): ArchitectureTemplate[] => {
  return ARCHITECTURE_TEMPLATES.filter(t => t.category === category);
};

// Helper to get template by id
export const getTemplateById = (id: string): ArchitectureTemplate | undefined => {
  return ARCHITECTURE_TEMPLATES.find(t => t.id === id);
};
